import { useEffect, useState, type FormEvent } from 'react'
import { createMaterial, createModule, listAllocations, listMaterials, listModules } from '@/lib/api'
import { useAsyncData } from '@/lib/useAsyncData'

export function LecturerMaterialsPage() {
  const allocations = useAsyncData(listAllocations, [])
  const modules = useAsyncData(listModules, [])
  const materials = useAsyncData(listMaterials, [])
  const [allocationId, setAllocationId] = useState('')
  const [moduleId, setModuleId] = useState('')
  const [moduleTitle, setModuleTitle] = useState('')
  const [materialTitle, setMaterialTitle] = useState('')
  const [link, setLink] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!allocationId && (allocations.data || [])[0]) {
      setAllocationId(String((allocations.data || [])[0].id))
    }
  }, [allocations.data, allocationId])

  useEffect(() => {
    const first = (modules.data || []).find((m) => String(m.course_allocation) === allocationId)
    if (first) setModuleId(String(first.id))
  }, [modules.data, allocationId])

  async function onCreateModule(e: FormEvent) {
    e.preventDefault()
    if (!allocationId || !moduleTitle.trim()) return
    setBusy(true)
    setMessage(null)
    try {
      await createModule({
        course_allocation: Number(allocationId),
        title: moduleTitle.trim(),
      })
      setModuleTitle('')
      setMessage('Module created.')
      await modules.reload()
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Failed')
    } finally {
      setBusy(false)
    }
  }

  async function onCreateMaterial(e: FormEvent) {
    e.preventDefault()
    if (!moduleId || !materialTitle.trim()) return
    setBusy(true)
    setMessage(null)
    try {
      const form = new FormData()
      form.append('module', moduleId)
      form.append('title', materialTitle.trim())
      if (link.trim()) form.append('external_link', link.trim())
      await createMaterial(form)
      setMaterialTitle('')
      setLink('')
      setMessage('Material added.')
      await materials.reload()
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-6 p-8">
      <h1 className="text-2xl font-bold text-slate-800">Notes & Materials</h1>
      {message && <p className="rounded-xl bg-slate-100 px-4 py-3 text-sm text-slate-700">{message}</p>}

      <div className="grid gap-4 lg:grid-cols-2">
        <form onSubmit={onCreateModule} className="space-y-3 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-bold text-slate-800">New module</h2>
          <select
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            value={allocationId}
            onChange={(e) => setAllocationId(e.target.value)}
          >
            {(allocations.data || []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.course_details.code}
              </option>
            ))}
          </select>
          <input
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Module title"
            value={moduleTitle}
            onChange={(e) => setModuleTitle(e.target.value)}
          />
          <button
            type="submit"
            disabled={busy}
            className="rounded-xl bg-emerald-600 px-4 py-2 text-xs font-semibold text-white"
          >
            Create module
          </button>
        </form>

        <form onSubmit={onCreateMaterial} className="space-y-3 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-bold text-slate-800">Add material</h2>
          <select
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            value={moduleId}
            onChange={(e) => setModuleId(e.target.value)}
          >
            <option value="">Select module…</option>
            {(modules.data || []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.course_code} — {m.title}
              </option>
            ))}
          </select>
          <input
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Material title"
            value={materialTitle}
            onChange={(e) => setMaterialTitle(e.target.value)}
          />
          <input
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="External link (optional)"
            value={link}
            onChange={(e) => setLink(e.target.value)}
          />
          <button
            type="submit"
            disabled={busy || !moduleId}
            className="rounded-xl bg-slate-800 px-4 py-2 text-xs font-semibold text-white disabled:bg-slate-300"
          >
            Add material
          </button>
        </form>
      </div>

      <div className="space-y-3">
        {(materials.data || []).map((m) => (
          <div key={m.id} className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold text-slate-800">{m.title}</p>
            <p className="text-xs text-slate-500">
              {m.course_code} · {m.module_title}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}
